import * as Sentry from '@sentry/browser';
import { User } from '@sr/types';
import React, { useEffect, useReducer } from 'react';

import { ArrowRightIcon, Box, Link, Text } from '../../ui';
import apiRequest from '../../utils/api-request';
import ProCancelModal from './ProCancelModal';

type Props = {
  user: User;
};

// For users that don't have a Stripe customer linked to their user
const NO_STRIPE_CUSTOMER = 'no-stripe-customer';

type SubscriptionStatus = 'free' | 'pro' | typeof NO_STRIPE_CUSTOMER | number;

type State = {
  loading: boolean;
  proUntil: number | null | undefined;
  subscriptionStatus: SubscriptionStatus | null | undefined;
};

type Action = {
  type: 'NEW_PRO_STATUS';
  status: SubscriptionStatus;
  userProUntil?: number;
};

function reducer(state: State, action: Action): State {
  const { status, type, userProUntil } = action;
  switch (type) {
    case 'NEW_PRO_STATUS':
      if (status === 'free') {
        return {
          ...state,
          loading: false,
          subscriptionStatus: 'free',
        };
      } else if (status === 'pro') {
        return {
          ...state,
          loading: false,
          subscriptionStatus: 'pro',
        };
      } else if (typeof status === 'number') {
        return {
          loading: false,
          subscriptionStatus: 'pro',
          proUntil: status,
        };
      } else if (status === NO_STRIPE_CUSTOMER) {
        // Users with proUntil but no Stripe customer. Either PMI users, or very old subscribers
        if (userProUntil && userProUntil > Date.now()) {
          return {
            loading: false,
            subscriptionStatus: NO_STRIPE_CUSTOMER,
            proUntil: userProUntil,
          };
        } else {
          // Users without an active pro subscription or Stripe customer
          return {
            ...state,
            loading: false,
            subscriptionStatus: 'free',
          };
        }
      } else {
        throw new Error(`Unhandled pro status: ${status}`);
      }
    default:
      throw new Error(`Unhandled type: ${type}`);
  }
}

function updateProStatus(user: User, dispatch: (arg0: any) => void) {
  getSubscriptionStatus(user)
    .then((result) => {
      dispatch({
        type: 'NEW_PRO_STATUS',
        status: result,
        userProUntil: user.proUntil,
      });
    })
    .catch((error) => {
      Sentry.captureException(error, { tags: { noFeedback: true } });
    });
}

const ProOptions = ({ user }: Props) => {
  // Pro status checks
  const [state, dispatch] = useReducer<React.Reducer<State, Action>>(reducer, {
    loading: true,
    proUntil: undefined,
    subscriptionStatus: undefined,
  });

  useEffect(() => {
    updateProStatus(user, dispatch);
  }, [state.proUntil, user]);

  const { loading, proUntil, subscriptionStatus } = state;

  if (loading) {
    return (
      <Box sx={{ lineHeight: '90px' }}>Subscription status loading...</Box>
    );
  }

  if (state.subscriptionStatus === NO_STRIPE_CUSTOMER && state.proUntil) {
    debugger;
    return (
      <Box mb={5}>
        <Text
          sx={{
            variant: 'text.base',
            color: 'black50',
            lineHeight: '32px',
          }}
        >
          Standard Resume Pro
        </Text>
        <Text variant="title">
          You have Pro access until {formatDate(state.proUntil)}.
        </Text>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 340, mb: 4 }}>
      {state.subscriptionStatus === 'free' && (
        <Link
          variant="primaryButton"
          to="/upgrade?refLink=accountPage"
          sx={{ justifyContent: 'space-between' }}
        >
          Upgrade to Pro
          <ArrowRightIcon color="white" />
        </Link>
      )}
      {subscriptionStatus === 'pro' && !proUntil && (
        <Box my={5} id="gi-pro-status-message">
          Your Pro subscription is currently active, you can{' '}
          <ProCancelModal
            user={user}
            onCancel={() => updateProStatus(user, dispatch)}
          />{' '}
          anytime.
        </Box>
      )}
      {subscriptionStatus === 'pro' && proUntil && (
        <Box my={5} id="gi-pro-status-message">
          {`Your Pro subscription has been canceled and will expire on ${formatDate(
            new window.Date(proUntil * 1000)
          )}.`}
        </Box>
      )}
    </Box>
  );
};

function formatDate(sourceDate: Date | number) {
  var monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  let date: Date;

  if (typeof sourceDate === 'number') {
    date = new Date(sourceDate);
  } else {
    date = sourceDate;
  }

  var day = date.getDate();
  var monthIndex = date.getMonth();
  var year = date.getFullYear();

  return `${monthNames[monthIndex]} ${day}, ${year}`;
}

async function getSubscriptionStatus(user: User): Promise<SubscriptionStatus> {
  if (!user.proUntil || user.proUntil < new window.Date().getTime()) {
    return Promise.resolve('free');
  } else {
    try {
      const { customer } = (await apiRequest('stripe/customer').json()) as {
        customer: any;
      };
      if (
        customer &&
        customer.subscriptions &&
        customer.subscriptions.data &&
        customer.subscriptions.data[0] &&
        customer.subscriptions.data[0]['cancel_at']
      ) {
        return customer.subscriptions.data[0]['cancel_at'];
      } else {
        return 'pro';
      }
    } catch (error) {
      return NO_STRIPE_CUSTOMER;
    }
  }
}

export default ProOptions;
